// contexts/ReferralContext.tsx
import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

// Constants
const ADMIN_PHONE_NUMBERS = ['+998991963315', '+998901234568']; // Add your admin phone numbers

// Types
export interface Referral {
  id?: string;
  code: string;
  discountPercentage: number;
  maxUses: number;
  currentUses: number;
  expiryDate: Date;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
}

interface ReferralState {
  referrals: Referral[];
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
}

type ReferralAction =
  | { type: 'SET_REFERRALS'; payload: Referral[] }
  | { type: 'ADD_REFERRAL'; payload: Referral }
  | { type: 'UPDATE_REFERRAL'; payload: Referral }
  | { type: 'DELETE_REFERRAL'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ADMIN'; payload: boolean };

interface ReferralContextType {
  state: ReferralState;
  createReferral: (referral: Omit<Referral, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'currentUses'>) => Promise<void>;
  updateReferral: (referral: Referral) => Promise<void>;
  deleteReferral: (referralId: string) => Promise<void>;
  checkIsAdmin: () => Promise<boolean>;
}

const initialState: ReferralState = {
  referrals: [],
  loading: false,
  error: null,
  isAdmin: false,
};

const ReferralContext = createContext<ReferralContextType | undefined>(undefined);

function referralReducer(state: ReferralState, action: ReferralAction): ReferralState {
  switch (action.type) {
    case 'SET_REFERRALS':
      return { ...state, referrals: action.payload, loading: false };
    case 'ADD_REFERRAL':
      return { ...state, referrals: [...state.referrals, action.payload], loading: false };
    case 'UPDATE_REFERRAL':
      return {
        ...state,
        referrals: state.referrals.map(r =>
          r.id === action.payload.id ? action.payload : r
        ),
        loading: false,
      };
    case 'DELETE_REFERRAL':
      return {
        ...state,
        referrals: state.referrals.filter(r => r.id !== action.payload),
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_ADMIN':
      return { ...state, isAdmin: action.payload };
    default:
      return state;
  }
}

export function ReferralProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(referralReducer, initialState);

  const checkIsAdmin = useCallback(async () => {
    const user = auth.currentUser;
    if (!user?.phoneNumber) return false;
    
    const isAdmin = ADMIN_PHONE_NUMBERS.includes(user.phoneNumber);
    dispatch({ type: 'SET_ADMIN', payload: isAdmin });
    return isAdmin;
  }, []);

  const createReferral = useCallback(async (referralData: Omit<Referral, 'id' | 'createdBy' | 'createdAt' | 'updatedAt' | 'currentUses'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      const currentDate = new Date();
      const newReferral: Omit<Referral, 'id'> = {
        ...referralData,
        currentUses: 0,
        createdBy: user.uid,
        createdAt: currentDate,
        updatedAt: currentDate,
      };

      const docRef = await addDoc(collection(db, 'referrals'), {
        ...newReferral,
        expiryDate: Timestamp.fromDate(newReferral.expiryDate),
        createdAt: Timestamp.fromDate(newReferral.createdAt),
        updatedAt: Timestamp.fromDate(newReferral.updatedAt),
      });

      dispatch({
        type: 'ADD_REFERRAL',
        payload: { ...newReferral, id: docRef.id },
      });
    } catch (error) {
      console.error('Error creating referral:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create referral' });
    }
  }, [checkIsAdmin]);

  const updateReferral = useCallback(async (referral: Referral) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      const { id, ...updateData } = referral;
      if (!id) throw new Error('Referral ID is required for update');

      const referralRef = doc(db, 'referrals', id);
      await updateDoc(referralRef, {
        ...updateData,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      dispatch({ type: 'UPDATE_REFERRAL', payload: referral });
    } catch (error) {
      console.error('Error updating referral:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update referral' });
    }
  }, [checkIsAdmin]);

  const deleteReferral = useCallback(async (referralId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      await deleteDoc(doc(db, 'referrals', referralId));
      dispatch({ type: 'DELETE_REFERRAL', payload: referralId });
    } catch (error) {
      console.error('Error deleting referral:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete referral' });
    }
  }, [checkIsAdmin]);

  const value = {
    state,
    createReferral,
    updateReferral,
    deleteReferral,
    checkIsAdmin,
  };

  return (
    <ReferralContext.Provider value={value}>
      {children}
    </ReferralContext.Provider>
  );
}

export function useReferral() {
  const context = useContext(ReferralContext);
  if (!context) {
    throw new Error('useReferral must be used within a ReferralProvider');
  }
  return context;
}